import { useState, useEffect, useCallback } from 'react';
import { services } from '../../../context';
import { AccountDetails } from '@v2/types';

interface UseAccountDetailsResult {
  account: AccountDetails | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAccountDetails = (accountId: string | null): UseAccountDetailsResult => {
  const [account, setAccount] = useState<AccountDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAccount = useCallback(async () => {
    if (!accountId) {
      setAccount(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const axiosInstance = services.axios.Axios();
      const response = await axiosInstance.get<AccountDetails>(`accounts/${accountId}`);
      setAccount(response.data || null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch account';
      setError(errorMessage);
      console.error('Error fetching account:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  return {
    account,
    loading,
    error,
    refetch: fetchAccount,
  };
};

export default useAccountDetails;
